import { ReactSimpleComponentProps } from "@/custom-types";
import { FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

type CenterScreenModal = ReactSimpleComponentProps & {
  isOpen: boolean
  onClose?: () => void
}

const closeIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z"/>
  </svg>
)

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  `
const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.light};
  padding: 2rem;
  border-radius: 8px;
  position: relative;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  max-height: 85%;
  overflow: auto;
  min-width: 350px;
`
const ModalCloseButton = styled.button`
  position: absolute;
  color: ${({ theme }) => theme.colors.primary1};
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1;
`

export const CenterScreenModal: FC<CenterScreenModal> = ({ children, isOpen, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current = document.createElement('div');
    document.body.appendChild(containerRef.current);

    return () => {
      if (containerRef.current) {
        document.body.removeChild(containerRef.current);
      }
    };
  }, [])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose])

  if (!isOpen || !containerRef.current) return null

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalCloseButton type="button" onClick={onClose}>{closeIcon}</ModalCloseButton>
        {children}
      </ModalContent>
    </ModalOverlay>,
    containerRef.current
  );
};

export default CenterScreenModal;
